import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { todayNZ } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, Loader2 } from "lucide-react";
import RoomCard from "@/pages/cleaner/RoomCard";

const isLive = (c) => {
  const st = c.session?.status;
  if (st === "in_progress" || st === "paused") return true;
  return (c.assist_sessions || []).some((a) => a.status === "in_progress" || a.status === "paused");
};

export default function CleanerView() {
  const { t } = useTranslation();
  const [cleanerId, setCleanerId] = useState("");
  const [tab, setTab] = useState("active");
  const date = todayNZ();

  const { data: cleaners = [] } = useQuery({
    queryKey: ["cleaners"],
    queryFn: async () => (await api.get("/cleaners")).data,
  });
  const { data: cleans = [], isLoading } = useQuery({
    queryKey: ["view-as", cleanerId, date],
    queryFn: async () => (await api.get(`/my/cleans?date=${date}&as_cleaner_id=${cleanerId}`)).data,
    enabled: !!cleanerId,
    refetchInterval: 15000,
  });

  const rooms = cleans.filter((c) => !c.is_strip_task);
  const mine = rooms.filter((c) => (c.my_role || "primary") !== "assist_available");
  const help = rooms.filter((c) => c.my_role === "assist_available");
  const progress = rooms.filter(isLive);
  const pid = new Set(progress.map((c) => c.id));
  const todo = mine.filter((c) => c.session?.status !== "completed" && !pid.has(c.id));
  const done = mine.filter((c) => c.session?.status === "completed" && !pid.has(c.id));
  const shown = tab === "progress" ? progress : tab === "completed" ? done : todo;
  const viewOnly = () => toast.info(t("View only — actions are disabled while viewing as a cleaner"));
  const cleaner = cleaners.find((c) => c.id === cleanerId);

  const Tab = ({ id, label, count }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`view-as-tab-${id}`}
      className={`k-press flex-1 rounded-md px-3 py-2 text-sm font-semibold ${tab === id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
    >
      {label} ({count})
    </button>
  );

  return (
    <div className="mx-auto max-w-lg space-y-4" data-testid="cleaner-view-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold">{t("View as cleaner")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("See exactly what a cleaner sees in their app today — in your language. Read-only.")}
        </p>
      </div>
      <Select value={cleanerId} onValueChange={(v) => { setCleanerId(v); setTab("active"); }}>
        <SelectTrigger data-testid="view-as-cleaner-select" className="h-11">
          <SelectValue placeholder={t("Choose a cleaner…")} />
        </SelectTrigger>
        <SelectContent>
          {cleaners.filter((c) => c.active !== false).map((c) => (
            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!cleanerId ? (
        <Card className="flex flex-col items-center gap-2 border-dashed py-12 text-center text-sm text-muted-foreground" data-testid="view-as-empty">
          <Eye className="h-6 w-6" /> {t("Pick a cleaner above to see their rooms and tasks.")}
        </Card>
      ) : isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <div className="flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-xs font-semibold text-indigo-900" data-testid="view-as-banner">
            <Eye className="h-4 w-4" /> {t("Viewing as {{name}} · {{done}}/{{total}} done", { name: cleaner?.name || "", done: done.length, total: mine.length })}
          </div>
          <div className="inline-flex w-full rounded-lg border border-border bg-card p-1" data-testid="view-as-tabs">
            <Tab id="active" label={t("To do")} count={todo.length} />
            <Tab id="progress" label={t("In progress / Assist")} count={progress.length} />
            <Tab id="completed" label={t("Completed")} count={done.length} />
          </div>
          {shown.length === 0 ? (
            <Card className="border-dashed py-10 text-center text-sm text-muted-foreground" data-testid="view-as-none">{t("Nothing here.")}</Card>
          ) : (
            <div className="space-y-4 k-compact" data-testid="view-as-rooms">
              {shown.map((c) => (
                <RoomCard
                  key={c.id}
                  clean={c}
                  meId={cleanerId}
                  onAct={viewOnly}
                  onReport={viewOnly}
                  onToggle={viewOnly}
                  onAssist={viewOnly}
                  onStrip={viewOnly}
                  onKeyReturn={viewOnly}
                  onDnd={viewOnly}
                  onUndoDnd={viewOnly}
                  onRelease={viewOnly}
                  onUndoStart={viewOnly}
                  onContinue={viewOnly}
                  onCompleteAllEnd={viewOnly}
                  onCompleteAllEndAssist={viewOnly}
                  isManager={false}
                />
              ))}
            </div>
          )}
          {help.length > 0 && tab === "active" && (
            <p className="text-xs text-muted-foreground" data-testid="view-as-help-count">
              {t("+ {{count}} room(s) they could help a teammate with", { count: help.length })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
